import {
  type AvailableAnswer,
  type RecommendedQuestion,
  SelectionType,
  QuestionSource,
  SelectionTypeView,
  type Recommendations,
} from '../../../sdk';
import type { AnswerRangeType } from '../../AnswerRange/AnswerRange';
import type { CustomQuestion } from '../../CustomQuestions/operations';

import { getAvailableAnswerOrders } from './selectors';
import type { Question } from './types';

export const EMPTY_PREFERRED_ANSWER = '';

export const TOGGLE_ESSENTIAL_QUESTION_SELECT =
  'TOGGLE_ESSENTIAL_QUESTION_SELECT';
export const SET_SELECTION_TYPE_VIEW = 'SET_SELECTION_TYPE_VIEW';
export const TOGGLE_QUESTION_SELECT = 'TOGGLE_QUESTION_SELECT';
export const TOGGLE_ANSWER_SELECT = 'TOGGLE_ANSWER_SELECT';
export const ANSWER_RANGE_SELECT = 'ANSWER_RANGE_SELECT';
export const SET_QUESTIONS_STATE = 'SET_QUESTIONS_STATE';
export const SET_VERSION_NUMBER = 'SET_VERSION_NUMBER';
export const ADD_SEARCH_QUESTION = 'ADD_SEARCH_QUESTION';
export const SHOW_QUESTIONS = 'SHOW_QUESTIONS';
export const DELETE_QUESTION = 'DELETE_QUESTION';
export const ADD_CUSTOM_QUESTION = 'ADD_CUSTOM_QUESTION';
export const SET_QUESTIONNAIRE_HAS_QUESTIONS_STATE =
  'SET_QUESTIONNAIRE_HAS_QUESTIONS_STATE';

interface AddCustomQuestionAction {
  type: typeof ADD_CUSTOM_QUESTION;
  question: CustomQuestion;
  order: number;
  source: typeof QuestionSource.Custom;
}

interface DeleteCustomQuestionAction {
  type: typeof DELETE_QUESTION;
  questionId: string;
  source: typeof QuestionSource.Custom;
}

interface AddSearchQuestion {
  type: typeof ADD_SEARCH_QUESTION;
  question: Question;
  source: typeof QuestionSource.Search;
}

interface ToggleSelectedQuestionAction {
  type: typeof TOGGLE_QUESTION_SELECT;
  questionId: string;
}

interface ToggleSelectedEssentialQuestionAction {
  type: typeof TOGGLE_ESSENTIAL_QUESTION_SELECT;
  questionId: string;
}

interface SetSelectionTypeView {
  type: typeof SET_SELECTION_TYPE_VIEW;
  questionId: string;
  selectionTypeView: SelectionTypeView;
}

interface ToggleSelectedAnswerAction {
  type: typeof TOGGLE_ANSWER_SELECT;
  questionId: string;
  answerId: string;
}

export interface SelectedAnswerRangeAction {
  type: typeof ANSWER_RANGE_SELECT;
  questionId: string;
  answerId: string;
  rangeType: AnswerRangeType;
}

interface QuestionsStateAction {
  type: typeof SET_QUESTIONS_STATE;
  questions: AllQuestions;
  selectionLimit: number;
  customQuestionsSelectionLimit: number;
  versionNumber: string;
  source: typeof QuestionSource.Library;
  questionSectionHeading: boolean;
  published: boolean;
  showCertsyTooltip: boolean;
}

interface VersionStateAction {
  type: typeof SET_VERSION_NUMBER;
  versionNumber: string;
}

interface ShowQuestionsAction {
  type: typeof SHOW_QUESTIONS;
}

type AllQuestions = Record<string, Question>;

interface QuestionnaireHasQuestionsStateAction {
  type: typeof SET_QUESTIONNAIRE_HAS_QUESTIONS_STATE;
  questionnaireHasQuestions: boolean;
}

export type Actions =
  | ToggleSelectedEssentialQuestionAction
  | ToggleSelectedQuestionAction
  | ToggleSelectedAnswerAction
  | SetSelectionTypeView
  | SelectedAnswerRangeAction
  | QuestionsStateAction
  | VersionStateAction
  | AddSearchQuestion
  | ShowQuestionsAction
  | DeleteCustomQuestionAction
  | AddCustomQuestionAction
  | QuestionnaireHasQuestionsStateAction;

export const addCustomQuestion = (
  question: CustomQuestion,
  order: number,
): AddCustomQuestionAction => ({
  type: ADD_CUSTOM_QUESTION,
  question,
  source: QuestionSource.Custom,
  order,
});

export const deleteCustomQuestion = (
  questionId: string,
): DeleteCustomQuestionAction => ({
  type: DELETE_QUESTION,
  questionId,
  source: QuestionSource.Custom,
});

export const toggleSelectedQuestion = (
  questionId: string,
): ToggleSelectedQuestionAction => ({
  type: TOGGLE_QUESTION_SELECT,
  questionId,
});

export const toggleSelectedEssentialQuestion = (
  questionId: string,
): ToggleSelectedEssentialQuestionAction => ({
  type: TOGGLE_ESSENTIAL_QUESTION_SELECT,
  questionId,
});

export const setSelectionTypeView = (
  questionId: string,
  selectionTypeView: SelectionTypeView,
): SetSelectionTypeView => ({
  type: SET_SELECTION_TYPE_VIEW,
  questionId,
  selectionTypeView,
});

export const toggleSelectedAnswer = (
  questionId: string,
  answerId: string,
): ToggleSelectedAnswerAction => ({
  type: TOGGLE_ANSWER_SELECT,
  questionId,
  answerId,
});

export const selectedAnswerRange = (
  questionId: string,
  answerId: string,
  rangeType: AnswerRangeType,
): SelectedAnswerRangeAction => ({
  type: ANSWER_RANGE_SELECT,
  questionId,
  answerId,
  rangeType,
});

export const addSearchQuestion = (
  question: RecommendedQuestion,
): AddSearchQuestion => ({
  type: ADD_SEARCH_QUESTION,
  question: {
    ...question,
    preferredAnswers: getPreferredAnswers(question),
    availableAnswerOrders: getAvailableAnswerOrders(question),
  },
  source: QuestionSource.Search,
});

interface QuestionState extends Recommendations {
  versionNumber: string;
}

export const setQuestions = ({
  questions,
  selectionLimit,
  customQuestionsSelectionLimit: customQuestionsSelectionLimit,
  versionNumber,
  published,
  questionSectionHeading,
  showCertsyVerifiableCopy,
}: QuestionState): QuestionsStateAction => ({
  type: SET_QUESTIONS_STATE,
  versionNumber,
  selectionLimit,
  customQuestionsSelectionLimit,
  source: QuestionSource.Library,
  questions: questions.reduce(
    (dict, q) => ({
      ...dict,
      [q.id]: {
        ...q,
        source: q.source,
        id: q.id,
        preferredAnswers: getPreferredAnswers(q),
        availableAnswerOrders: getAvailableAnswerOrders(q),
      },
    }),
    {} as AllQuestions,
  ),
  questionSectionHeading,
  published,
  showCertsyTooltip: showCertsyVerifiableCopy ?? false,
});

export const setVersionNumber = (
  versionNumber: string,
): VersionStateAction => ({
  type: SET_VERSION_NUMBER,
  versionNumber,
});

export const showQuestions = (): ShowQuestionsAction => ({
  type: SHOW_QUESTIONS,
});

export const setQuestionnaireHasQuestions = (
  questionnaireHasQuestions: boolean,
): QuestionnaireHasQuestionsStateAction => ({
  type: SET_QUESTIONNAIRE_HAS_QUESTIONS_STATE,
  questionnaireHasQuestions,
});

const getPreferredAnswers = (question: RecommendedQuestion) =>
  question.selectionType === SelectionType.Range
    ? getPreferredAnswerRange(question.availableAnswers)
    : {
        [SelectionTypeView.Answers]: question.availableAnswers
          .filter((a) => a.preferred)
          .map((a) => a.id),
        [SelectionTypeView.SimplifiedAnswers]: question.simplifiedAnswers
          .filter((a) => a.preferred)
          .map((a) => a.id),
      };

const getPreferredAnswerRange = (
  availableAnswers: AvailableAnswer[],
): { [key in SelectionTypeView]: string[] } => {
  const preferredAnswers = availableAnswers.filter((x) => x.preferred);
  const [maximumAvailableAnswer] = availableAnswers.slice(-1);

  const isMaximumPreferredAnswer =
    preferredAnswers.length === 1 &&
    preferredAnswers[0].id === maximumAvailableAnswer.id;

  if (preferredAnswers.length === 0 || isMaximumPreferredAnswer) {
    return {
      [SelectionTypeView.Answers]: [],
      [SelectionTypeView.SimplifiedAnswers]: [],
    };
  }

  const minAnswer = preferredAnswers[0];
  const [maxAnswer] = preferredAnswers.slice(-1);

  if (minAnswer.id === maxAnswer.id) {
    return {
      [SelectionTypeView.Answers]: [minAnswer.id, minAnswer.id],
      [SelectionTypeView.SimplifiedAnswers]: [],
    };
  }

  return {
    [SelectionTypeView.Answers]: [minAnswer.id, maxAnswer.id],
    [SelectionTypeView.SimplifiedAnswers]: [],
  };
};
